import { BaseWbsItem } from '~/_base/constants/api';

export interface WbsItem extends BaseWbsItem {
  parents: string[];
  hasChildren?: boolean;
}

export type InternalWBS = ReturnType<typeof transformWbsData>;

export function getParents(wbsItem: BaseWbsItem) {
  const orderArr = wbsItem.order.split('|');
  let currentLevel = orderArr[0];
  let parents = [];
  for (let i = 0; i < orderArr.length - 1; i++) {
    parents.push(currentLevel);
    currentLevel = `${currentLevel}|${orderArr[i + 1]}`;
  }
  return parents;
}

export default function transformWbsData(wbsData: BaseWbsItem[]) {
  let byLevel: { [level: string]: WbsItem } = {};
  // Build an object keyed by each items level
  wbsData.forEach(
    item => (byLevel[item.order] = { ...item, parents: getParents(item) })
  );
  wbsData.forEach(wbsItem => {
    const itemWithParents = byLevel[wbsItem.order];
    let counter = -1;
    // For each item, loop through each prior level
    // I.e. for "0001|0001|0001", we would loop through "0001", and "0001|0001"
    itemWithParents.parents.forEach((level, i) => {
      const hasChildren = i < itemWithParents.parents.length;
      if (byLevel[level] == null) {
        // No item exists at this level, create a dummy item
        const dummyNumber = wbsItem.number
          .split('.')
          .slice(0, i + 1)
          .join('.');
        const dummyItem = {
          id: counter,
          name: dummyNumber,
          number: dummyNumber,
          order: level,
          project_id: wbsItem.project_id,
          hasChildren,
        };
        byLevel[level] = { ...dummyItem, parents: getParents(dummyItem) };
      } else {
        byLevel[level].hasChildren = hasChildren;
      }
      counter--;
    });
  });

  const allLevels = Object.keys(byLevel);
  const allItems = Object.values(byLevel);

  function getAllChildren(level: string) {
    return allLevels
      .filter(l => l.startsWith(level) && l.length > level.length)
      .map(l => byLevel[l])
      .sort((a, b) => a.order.localeCompare(b.order));
  }

  function getChildren(level: string) {
    return allLevels
      .filter(l => {
        const isChild = l.split('|').length === level.split('|').length + 1;
        return l.startsWith(level) && isChild;
      })
      .map(l => byLevel[l])
      .sort((a, b) => a.order.localeCompare(b.order));
  }

  function getTopElements() {
    return allLevels
      .filter(l => !l.includes('|'))
      .map(l => byLevel[l])
      .sort((a, b) => a.order.localeCompare(b.order));
  }

  return {
    all: allItems,
    byLevel: byLevel,
    getAllChildren,
    getChildren,
    topElements: getTopElements(),
  };
}
